import { CartAddEvent } from '@theme/events';
import { buildGiftItems } from '@theme/zrzs-quick-add-gifts';
import { QUICK_ADD_CART_SECTION_ID } from '@theme/zrzs-quick-add-cart-panel';

/**
 * Mesma normalização de assets/lens-selection.js#slugify.
 * @param {string | undefined} value
 * @returns {string}
 */
function slugify(value) {
  return (value || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Mesma convenção de assets/lens-selection.js#generateLensHandle.
 * @param {string} productSlug
 * @param {string} variantTitle
 * @returns {string}
 */
function generateLensHandle(productSlug, variantTitle) {
  return `${productSlug}-${slugify(variantTitle)}`;
}

// Mesma chave de assets/lens-selection.js#VTO_TRIAL_AT_PREFIX, lida pelo widget de try-on virtual.
const VTO_TRIAL_AT_PREFIX = '_vtoTrialAt_';

/**
 * Mesma leitura de assets/lens-selection.js#getVtoTrialTimestampForVariant.
 * @param {string | number} frameVariantId
 * @returns {string | null}
 */
function getVtoTrialTimestampForVariant(frameVariantId) {
  try {
    return localStorage.getItem(`${VTO_TRIAL_AT_PREFIX}${frameVariantId}`);
  } catch {
    return null;
  }
}

/**
 * `_grupo` identifica quais kit/lente/armação pertencem à mesma compra —
 * `_productVariantTitleValue` não basta, já que duas compras da mesma
 * armação com lentes diferentes compartilham esse valor. Precisa ser
 * determinístico: duas compras com a mesma configuração devem gerar o
 * mesmo `_grupo`, senão o Shopify nunca as mescla numa linha só.
 * @param {Array<string | number | null | undefined>} parts
 * @returns {string}
 */
function buildGroupId(parts) {
  return parts
    .map(part => (part === null || part === undefined ? '' : String(part)))
    .join('::');
}

/**
 * Cilíndrico/eixo em branco (sem astigmatismo) vira "0" aqui — mesma
 * normalização já usada na exibição (ver formatPrescriptionSummaryLine em
 * zrzs-quick-add-lens-flow.js). Sem isso, a property armazenada guardava
 * string vazia enquanto a tela já mostrava "0", uma divergência entre o
 * que o cliente vê e o dado que chega pro time de produção/laboratório.
 * @param {string | null | undefined} raw
 */
function normalizeOptionalPrescriptionValue(raw) {
  return raw && raw.trim() !== '' ? raw : '0';
}

/**
 * @param {{ odSphere: string, odCylinder: string, odAxis: string, oeSphere: string, oeCylinder: string, oeAxis: string } | null | undefined} prescription
 * @returns {string | null}
 */
function buildPrescriptionProperty(prescription) {
  if (!prescription) return null;
  return JSON.stringify({
    od: {
      esf: prescription.odSphere,
      cil: normalizeOptionalPrescriptionValue(prescription.odCylinder),
      eixo: normalizeOptionalPrescriptionValue(prescription.odAxis),
    },
    oe: {
      esf: prescription.oeSphere,
      cil: normalizeOptionalPrescriptionValue(prescription.oeCylinder),
      eixo: normalizeOptionalPrescriptionValue(prescription.oeAxis),
    },
  });
}

/**
 * POST /cart/add.js — compartilhado pelos dois fluxos de adição abaixo.
 * @param {Array<{id: string, quantity: number, properties: Record<string, string>}>} items
 * @param {string} eventVariantId variante principal do grupo (a armação).
 * @returns {Promise<{ items: Array<{id: string, quantity: number, properties: Record<string, string>}>, cart: any }>}
 */
async function postCartAdd(items, eventVariantId) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items,
      attributes: { is_ecomm: true },
      // HTML do painel do carrinho já atualizado, no padrão do tema — vai no
      // CartAddEvent e o painel aplica sem buscar de novo.
      sections: QUICK_ADD_CART_SECTION_ID,
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Error ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage += `: ${errorData.message || 'Unknown error'}`;
    } catch {
      const errorText = await response.text();
      if (errorText) errorMessage += `: ${errorText}`;
    }
    throw new Error(errorMessage);
  }

  let cart = null;
  try {
    cart = await response.json();
  } catch {
    // body vazio
  }

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  document.dispatchEvent(
    new CartAddEvent({}, eventVariantId, {
      itemCount,
      source: 'product-form-component',
      variantId: eventVariantId,
      sections: cart?.sections,
    })
  );

  try {
    document.dispatchEvent(
      new CustomEvent('zrzs:add-to-cart', { detail: { items } })
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `|z:z| Error dispatching zrzs:add-to-cart event: ${error.message}`
    );
  }

  return { items, cart };
}

/** @type {Promise<any>} */
let cartAddQueue = Promise.resolve();

/**
 * Serializa cada adição (buildGiftItems lê /cart.js, postCartAdd escreve
 * nele) — sem isso, 2 adições concorrentes (2 cards clicados quase
 * juntos) liam o MESMO snapshot "brinde ainda não está no carrinho" e as
 * duas decidiam adicioná-lo, duplicando a quantidade (bug real). A 2ª só
 * lê o carrinho depois que a 1ª termina de escrever.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function queueCartAdd(task) {
  const run = cartAddQueue.catch(() => {}).then(task);
  cartAddQueue = run;
  return run;
}

/**
 * @param {Parameters<typeof addFrameWithKitToCartImpl>[0]} params
 * @returns {Promise<{ items: Array<{id: string, quantity: number, properties: Record<string, string>}>, cart: any, hasLensBasket: boolean }>}
 */
export function addFrameWithKitToCart(params) {
  return queueCartAdd(() => addFrameWithKitToCartImpl(params));
}

/**
 * @param {object} params
 * @param {string | number} params.frameVariantId
 * @param {string} params.frameVariantTitle
 * @param {string | number | null} [params.kitVariantId]
 * @param {boolean} [params.hasLensBasket] não usado aqui; repassado pro chamador (zrzs-quick-add-overlay.js) indicar que a lente ainda falta.
 * @param {string | null} [params.kitCaseBanner] convenção de assets/lens-selection.js#addToCart — só o kit recebe.
 * @param {string | null} [params.kitCaseDescription]
 * @param {number} [params.framePriceCents] usado na faixa de valor (min/max_cart_value) dos brindes — ver zrzs-quick-add-gifts.js.
 * @param {any[] | null} [params.giftsData] mesmo formato de assets/lens-selection.js#giftsData.
 */
async function addFrameWithKitToCartImpl({
  frameVariantId,
  frameVariantTitle,
  kitVariantId,
  hasLensBasket = false,
  kitCaseBanner,
  kitCaseDescription,
  framePriceCents,
  giftsData,
}) {
  if (!frameVariantId) {
    throw new Error('addFrameWithKitToCart: frameVariantId is required');
  }

  // Sem lente aqui — a identidade da compra é só armação + kit.
  const groupId = buildGroupId([frameVariantId, kitVariantId]);
  const productVariantTitleValue = `${frameVariantTitle} - ${frameVariantId}`;
  const vtoTestedAt = getVtoTrialTimestampForVariant(frameVariantId);

  const { giftItems, frameGiftProperties } = await buildGiftItems({
    giftsData,
    frameSelfMarker: productVariantTitleValue,
    pendingAdditionsTotal: framePriceCents || 0,
  });

  /** @type {Array<{id: string, quantity: number, properties: Record<string, string>}>} */
  const items = [...giftItems];

  // Kit antes do frame, igual à ordem usada em lens-selection.js.
  if (kitVariantId) {
    items.push({
      id: kitVariantId.toString(),
      quantity: 1,
      properties: {
        _productVariantTitleValue: productVariantTitleValue,
        _grupo: groupId,
        ...(kitCaseBanner ? { _caseBanner: kitCaseBanner } : {}),
        ...(kitCaseDescription ? { _caseDescription: kitCaseDescription } : {}),
      },
    });
  }

  items.push({
    id: frameVariantId.toString(),
    quantity: 1,
    properties: {
      _productVariantTitleValue: productVariantTitleValue,
      _grupo: groupId,
      ...(vtoTestedAt ? { _vto_tested_at: vtoTestedAt } : {}),
      ...frameGiftProperties,
    },
  });

  const { cart } = await postCartAdd(items, frameVariantId.toString());

  return { items, cart, hasLensBasket };
}

/**
 * Monta e envia o /cart/add.js do fluxo de grau (brindes + lente + kit +
 * armação numa chamada só), mesma ordem/convenção de
 * assets/lens-selection.js#addToCart. Sem upsell aqui (fora de escopo).
 * @param {Parameters<typeof addFrameWithLensToCartImpl>[0]} params
 * @returns {Promise<{ items: Array<{id: string, quantity: number, properties: Record<string, string>}>, cart: any }>}
 */
export function addFrameWithLensToCart(params) {
  return queueCartAdd(() => addFrameWithLensToCartImpl(params));
}

/**
 * @param {object} params
 * @param {string | number} params.frameVariantId
 * @param {string} params.frameVariantTitle
 * @param {string | number | null} [params.kitVariantId]
 * @param {{ variantRef: string | number | null, productSlug: string | null, variantTitle: string | null }} params.lens variante final (LensFlowController#finalLensSelection).
 * @param {{ odSphere: string, odCylinder: string, odAxis: string, oeSphere: string, oeCylinder: string, oeAxis: string } | null} [params.prescription]
 * @param {string | null} [params.kitCaseBanner] convenção de assets/lens-selection.js#addToCart — só o kit recebe.
 * @param {string | null} [params.kitCaseDescription]
 * @param {number} [params.framePriceCents] usado na faixa de valor (min/max_cart_value) dos brindes — ver zrzs-quick-add-gifts.js.
 * @param {any[] | null} [params.giftsData] mesmo formato de assets/lens-selection.js#giftsData.
 */
async function addFrameWithLensToCartImpl({
  frameVariantId,
  frameVariantTitle,
  kitVariantId,
  lens,
  prescription,
  kitCaseBanner,
  kitCaseDescription,
  framePriceCents,
  giftsData,
}) {
  if (!frameVariantId) {
    throw new Error('addFrameWithLensToCart: frameVariantId is required');
  }
  if (!lens || !lens.variantRef || !lens.productSlug || !lens.variantTitle) {
    throw new Error('addFrameWithLensToCart: lens data is incomplete');
  }

  const frameSelfMarker = `${frameVariantTitle} - ${frameVariantId}`;
  const lensHandle = generateLensHandle(lens.productSlug, lens.variantTitle);
  const framePropertyValue = `armacao-com-${lensHandle}`;
  const prescriptionProperty = buildPrescriptionProperty(prescription);
  // Identidade da compra aqui: armação + kit + lente + receita (se houver).
  const groupId = buildGroupId([
    frameVariantId,
    kitVariantId,
    lens.variantRef,
    prescriptionProperty,
  ]);
  const vtoTestedAt = getVtoTrialTimestampForVariant(frameVariantId);
  // Mesma convenção de assets/lens-selection.js#addToCart: guarda o variant
  // id real da lente na própria armação, pra uma extensão de checkout (ou
  // outro processo) poder readicioná-la caso suma do carrinho.
  const lensVariantId = parseInt(String(lens.variantRef), 10);
  const lensVariantIdProperty = isNaN(lensVariantId)
    ? null
    : String(lensVariantId);

  const { giftItems, frameGiftProperties } = await buildGiftItems({
    giftsData,
    frameSelfMarker,
    pendingAdditionsTotal: (framePriceCents || 0) + (lens.priceCents || 0),
  });

  /** @type {Array<{id: string, quantity: number, properties: Record<string, string>}>} */
  const items = [...giftItems];

  items.push({
    id: lens.variantRef.toString(),
    quantity: 1,
    properties: {
      _lente: lensHandle,
      _productVariantTitleValue: frameSelfMarker,
      _grupo: groupId,
      ...(prescriptionProperty ? { _receita: prescriptionProperty } : {}),
    },
  });

  if (kitVariantId) {
    items.push({
      id: kitVariantId.toString(),
      quantity: 1,
      properties: {
        _productVariantTitleValue: frameSelfMarker,
        _armacao: framePropertyValue,
        _grupo: groupId,
        ...(kitCaseBanner ? { _caseBanner: kitCaseBanner } : {}),
        ...(kitCaseDescription ? { _caseDescription: kitCaseDescription } : {}),
      },
    });
  }

  items.push({
    id: frameVariantId.toString(),
    quantity: 1,
    properties: {
      _productVariantTitleValue: frameSelfMarker,
      _armacao: framePropertyValue,
      _grupo: groupId,
      ...(lensVariantIdProperty ? { _lensVariantId: lensVariantIdProperty } : {}),
      ...(vtoTestedAt ? { _vto_tested_at: vtoTestedAt } : {}),
      ...frameGiftProperties,
    },
  });

  const { cart } = await postCartAdd(items, frameVariantId.toString());

  return { items, cart };
}
